import React, { useState, useMemo, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { File, Download, X, ZoomIn, ZoomOut, Maximize2 } from 'react-feather';
import logger from '@/logger';
import Modal from '@/UIlibrary/Modal/Modal';
import { useTranslations } from 'use-intl';
import { toNamespaceKey, toTranslationKey } from '@/utils/translations';
import Button from '@/UIlibrary/Button/Button';
import * as Sentry from '@sentry/react';

interface FileEntry {
	url: string;
	label?: string;
}

interface MediaCardProps {
	title: string;
	files: FileEntry[];
	translationKey: string;
	mimeType?: 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/jpg';
	icon?: React.ReactNode;
	checkbox?: React.ReactNode;
	reloadURL?: (fileName: string) => Promise<string>;
}

const MediaCard: React.FC<MediaCardProps> = ({
	title,
	files,
	translationKey,
	mimeType: providedMimeType,
	icon,
	checkbox,
	reloadURL
}) => {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [fullscreenImage, setFullscreenImage] = useState<{ url: string; label: string } | null>(
		null
	);
	const [zoomLevel, setZoomLevel] = useState(1);

	const t = useTranslations(toNamespaceKey(translationKey));

	const mainFile = files[0]?.url;
	const hasMultipleFiles = files.length > 1;

	const mimeType = useMemo(() => {
		if (providedMimeType) return providedMimeType;

		const urlWithoutParams = mainFile?.split('?')[0];
		const extension = urlWithoutParams?.split('.').pop()?.toLowerCase();

		switch (extension) {
			case 'pdf':
				return 'application/pdf';
			case 'jpg':
			case 'jpeg':
				return 'image/jpeg';
			case 'png':
				return 'image/png';
			default:
				logger.error('Unknown file extension', { extension, url: mainFile });
				return 'application/pdf';
		}
	}, [mainFile, providedMimeType]);

	const isImage = mimeType.startsWith('image/');

	const handlePreview = (e: React.MouseEvent) => {
		// Check if the click originated from the checkbox container
		const checkboxContainer = document.querySelector('.checkbox-container');
		if (checkboxContainer?.contains(e.target as Node)) {
			return;
		}

		setIsPreviewOpen(true);
		logger.info('MediaCard preview opened', { title, mimeType });
	};

	const handleDownload = (url: string, fileLabel: string) => async (e: React.MouseEvent) => {
		e.preventDefault();
		setDownloadLoading(true);
		try {
			let downloadUrl = url;
			if (reloadURL) {
				const newUrl = await reloadURL(fileLabel);
				downloadUrl = newUrl;
			}

			const link = document.createElement('a');
			link.href = downloadUrl || url;
			link.setAttribute('download', `${title}_${fileLabel}`);
			link.setAttribute('target', '_blank');
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			logger.info('MediaCard file downloaded', { title, fileLabel, mimeType });
		} catch (error) {
			logger.error('MediaCard download failed', { title, fileLabel, mimeType }, error);
			Sentry.captureException(error);
		} finally {
			setDownloadLoading(false);
		}
	};

	const handleImageClick = (url: string, label: string) => {
		setFullscreenImage({ url, label });
		setZoomLevel(1);
	};

	const handleCloseFullscreen = () => {
		setFullscreenImage(null);
		setZoomLevel(1);
	};

	const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3));
	const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
	const handleResetZoom = () => setZoomLevel(1);

	useEffect(() => {
		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && fullscreenImage) {
				handleCloseFullscreen();
			}
		};

		if (fullscreenImage) {
			document.addEventListener('keydown', handleEscapeKey);
		}

		return () => {
			document.removeEventListener('keydown', handleEscapeKey);
		};
	}, [fullscreenImage]);

	const renderThumbnail = () => {
		if (isImage && mainFile) {
			return (
				<div className="relative">
					<img src={mainFile} alt={title} className="w-full h-32 object-cover rounded-md" />
					{hasMultipleFiles && files?.[1].url && (
						<div className="absolute -bottom-2 -right-2 w-16 h-16 rounded-md overflow-hidden border-2 border-white shadow-md">
							<img
								src={files[1].url}
								alt={`${title} additional`}
								className="w-full h-full object-cover"
							/>
						</div>
					)}
				</div>
			);
		}
		if (mimeType === 'application/pdf' && mainFile) {
			return (
				<div className="relative w-full h-32 rounded-md overflow-hidden" onClick={handlePreview}>
					<iframe
						src={mainFile}
						className="w-full h-48 -mt-8 scale-75 origin-top pointer-events-none"
						title={`${title} thumbnail`}
					/>
					<div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/30" />
				</div>
			);
		}
		return (
			<div className="w-full h-32 bg-slate-100 rounded-md flex items-center justify-center">
				<File size={48} className="text-slate-400" />
			</div>
		);
	};

	const renderPreview = () => {
		if (isImage) {
			return (
				<div className="flex flex-col gap-4 w-full">
					<div className="flex justify-between items-center">
						<Typography size="xl" weight="semibold">
							{title}
						</Typography>
					</div>
					<div className="flex flex-col justify-center gap-4">
						{files.map((file, index) => (
							<div key={file.url} className="flex flex-col items-center gap-2">
								{file?.url && (
									<>
										<div className="relative group">
											<img
												src={file.url}
												alt={`${title} ${file.label || index + 1}`}
												className="max-w-[300px] max-h-[300px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
												onClick={() => handleImageClick(file.url, file.label || `${index + 1}`)}
											/>
											<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
												<div className="bg-black/50 rounded-full p-2">
													<Maximize2 size={24} className="text-white" />
												</div>
											</div>
										</div>
										<Button
											onClick={handleDownload(file.url, file.label || `${index + 1}`)}
											label={t(toTranslationKey('Download'))}
											endIcon={<Download size={16} />}
											loading={downloadLoading}
										/>
									</>
								)}
							</div>
						))}
					</div>
				</div>
			);
		}
		if (mimeType === 'application/pdf' && mainFile) {
			return (
				<div className="flex flex-col gap-4 w-full">
					<div className="flex justify-between items-center">
						<Typography size="xl" weight="semibold">
							{title}
						</Typography>
					</div>
					<iframe src={mainFile} className="w-full h-[70vh]" title={title} />
				</div>
			);
		}
		return null;
	};

	return (
		<>
			<div
				className={`flex flex-col bg-white rounded-lg p-4 md:p-6 gap-4 shadow ${
					mainFile ? 'hover:bg-slate-100 cursor-pointer' : ''
				} w-64 md:w-full`}
				onClick={mainFile ? handlePreview : undefined}
			>
				<div className="flex flex-col grow gap-4">
					<div className="flex flex-row items-center gap-2 min-h-14">
						{icon && icon}
						<Typography size="xl" weight="semibold">
							{title}
						</Typography>
					</div>
					<div className="flex flex-col gap-2 min-h-40">
						{mainFile ? (
							renderThumbnail()
						) : (
							<div className="w-full h-32 bg-slate-50 rounded-md flex items-center justify-center">
								<img src="/noData.svg" alt="" />
							</div>
						)}
						{checkbox && (
							<div
								className="flex justify-center checkbox-container"
								onClick={(e) => e.stopPropagation()}
							>
								{checkbox}
							</div>
						)}
					</div>
				</div>

				<Typography size="xs" color="subtitle">
					{mainFile
						? t(toTranslationKey('ClickToVisualize'))
						: t(toTranslationKey('NoFileUploaded'))}
				</Typography>
			</div>
			<Modal
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				closeable={true}
				maxWidth={1024}
			>
				{renderPreview()}
			</Modal>

			{fullscreenImage && (
				<div
					className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center"
					onClick={handleCloseFullscreen}
				>
					<button
						onClick={handleCloseFullscreen}
						className="absolute top-4 right-4 text-white hover:bg-white/10 rounded-full p-2 transition-colors z-10"
					>
						<X size={32} />
					</button>

					<div className="absolute top-4 left-4 flex gap-2 z-10">
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleZoomIn();
							}}
							className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
						>
							<ZoomIn size={24} />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleZoomOut();
							}}
							className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
						>
							<ZoomOut size={24} />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleResetZoom();
							}}
							className="text-white hover:bg-white/10 rounded px-3 py-2 transition-colors text-sm"
						>
							{Math.round(zoomLevel * 100)}%
						</button>
					</div>

					<div
						className="overflow-auto max-w-full max-h-full p-4"
						onClick={(e) => e.stopPropagation()}
					>
						<img
							src={fullscreenImage.url}
							alt={`${title} ${fullscreenImage.label}`}
							className="transition-transform duration-200"
							style={{ transform: `scale(${zoomLevel})` }}
						/>
					</div>

					<div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
						<Button
							onClick={async (e) => {
								e.stopPropagation();
								await handleDownload(fullscreenImage.url, fullscreenImage.label)(e);
							}}
							label={t(toTranslationKey('Download'))}
							endIcon={<Download size={16} />}
							loading={downloadLoading}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default MediaCard;
